import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useLang } from "../i18n/LanguageContext";
import { useAuth } from "../auth/AuthContext";
import { fetchHistory, fetchHistoryDetail } from "../lib/api";
import { statusFor } from "../lib/status";
import { ResultCard } from "../components/ResultCard";
import { Loader2, ChevronLeft, LogOut, History as HistoryIcon } from "lucide-react";

export default function History() {
  const { t, lang } = useLang();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [detail, setDetail] = useState(null);

  useEffect(() => { if (user === null) navigate("/login"); }, [user, navigate]);

  const load = useCallback(async () => {
    try { const d = await fetchHistory(); setItems(d.items); } catch (_) { setItems([]); }
  }, []);
  useEffect(() => { if (user) load(); }, [user, load]);

  const openDetail = async (id) => {
    setDetail("loading");
    try { setDetail(await fetchHistoryDetail(id)); } catch (_) { setDetail(null); }
  };

  const fmtDate = (iso) => { try { return new Date(iso).toLocaleString(lang); } catch { return iso; } };

  if (!user) return null;

  return (
    <div className="min-h-screen pt-28 pb-24 sm:pt-32">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">{t("history.title")}</h1>
            <p className="mt-2 text-sm text-slate-500">{t("history.subtitle")}</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-500">{user.email}</span>
            <Link data-testid="account-data-link" to="/account" className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100">
              {t("account.dataLink")}
            </Link>
            <button data-testid="logout-btn" onClick={() => { logout(); navigate("/"); }} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100">
              <LogOut className="h-4 w-4" /> {t("account.logout")}
            </button>
          </div>
        </div>

        {detail ? (
          <div className="mt-8">
            <button onClick={() => setDetail(null)} className="mb-4 inline-flex items-center gap-1.5 text-sm text-blue-700 hover:text-blue-700">
              <ChevronLeft className="h-4 w-4" /> {t("history.back")}
            </button>
            {detail === "loading" ? (
              <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-blue-700" /></div>
            ) : (
              <ResultCard result={detail} />
            )}
          </div>
        ) : items === null ? (
          <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-blue-700" /></div>
        ) : items.length === 0 ? (
          <div className="mt-12 flex flex-col items-center rounded-3xl border border-slate-200 bg-slate-50 py-16 text-center" data-testid="history-empty">
            <HistoryIcon className="h-10 w-10 text-slate-600" />
            <p className="mt-4 text-slate-500">{t("history.empty")}</p>
            <Link to="/check" className="mt-5 rounded-full bg-[#1E3A8A] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#1D4ED8]">{t("history.emptyCta")}</Link>
          </div>
        ) : (
          <div className="mt-8 space-y-3" data-testid="history-list">
            {items.map((it, i) => {
              const s = statusFor(it.risk_level || "LIMITED");
              const Icon = s.icon;
              return (
                <button
                  key={it.analysis_id}
                  data-testid={`history-item-${i}`}
                  onClick={() => openDetail(it.analysis_id)}
                  className="flex w-full items-center gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-left transition-colors hover:bg-slate-50"
                >
                  <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${s.bg} ring-1 ${s.ring}`}>
                    <Icon className={`h-5 w-5 ${s.text}`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-bold ${s.text}`}>{it.risk_level ? t(`common.${it.risk_level.toLowerCase()}`) : "—"}</span>
                      {it.risk_score != null && <span className="font-mono text-xs text-slate-500">{it.risk_score}</span>}
                      <span className="text-xs text-slate-600">· {fmtDate(it.created_at)}</span>
                    </div>
                    <p className="mt-0.5 truncate text-sm text-slate-500">{it.preview || t(`check.tabs.${it.input_type}`)}</p>
                  </div>
                  <span className="hidden shrink-0 text-xs text-blue-700 sm:inline">{t("history.view")}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
